// Winks bundle buy box: one-time quantity tiers with a page-scoped discount code.
// Adds bags (+ zero-dollar gift entitlements when the tier includes them), then
// routes through /discount/{code} so Shopify applies the tier's Buy X Get Y code.

// Set the offer's bag count rather than adding to it: a shopper who returns
// from checkout and submits again must still have exactly the bundle's bags,
// since the code frees bags only once per order. Once a Buy X Get Y code has
// applied, Shopify splits the bags into a paid line and a free line, so every
// one-time line of this variant is counted and collapsed, never just the first.
// Subscription lines and other products are left alone. After the write the
// cart is re-read and corrected, so two tabs adding at once cannot leave more
// bags than the code frees.
async function setBundleLine(fetchFn, root, base) {
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const readOneTimeLines = async () => {
    const cartResponse = await fetchFn(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(`Cart read failed (${cartResponse.status})`);
    const cart = await cartResponse.json();
    return (cart.items || []).filter(
      (line) => Number(line.variant_id) === base.id && !line.selling_plan_allocation,
    );
  };
  const total = (lines) => lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  // One update call: the first one-time line carries the tier's bag count and
  // every other one-time line of this variant is cleared.
  const collapse = (lines) => fetchFn(`${root}cart/update.js`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({
      updates: Object.fromEntries(lines.map((line, index) => [line.key, index === 0 ? base.quantity : 0])),
    }),
  });

  const existing = await readOneTimeLines();
  const response = existing.length
    ? await collapse(existing)
    : await fetchFn(`${root}cart/add.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items: [base] }),
    });
  if (!response.ok) return response;

  const settled = await readOneTimeLines();
  if (settled.length && total(settled) !== base.quantity) {
    const fix = await collapse(settled);
    if (!fix.ok) throw new Error(`Cart correction failed (${fix.status})`);
  }
  return response;
}

function initBundleForm(form) {
  if (form.dataset.winksBundleReady) return;
  form.dataset.winksBundleReady = 'true';

  const tiers = [...form.querySelectorAll('[data-winks-bundle-tier]')];
  const gifts = form.querySelector('[data-winks-bundle-gifts]');
  const button = form.querySelector('[data-winks-bundle-submit]');
  const error = form.querySelector('[data-winks-bundle-error]');
  const labelNode = button?.querySelector('.add-to-cart-text__content') || button;
  const label = labelNode?.textContent;
  const priceNode = form.querySelector('[data-winks-bundle-cta-price]');

  const selectedInput = () => form.querySelector('.winks-bundle-tier__input:checked');

  const sync = () => {
    const input = selectedInput();
    tiers.forEach((tier) => tier.classList.toggle('is-selected', tier.contains(input)));
    if (gifts) gifts.hidden = input?.dataset.gifts !== 'true';
    if (priceNode && input) priceNode.textContent = input.dataset.save ? `· Save ${input.dataset.save}%` : '';
  };

  tiers.forEach((tier) => tier.querySelector('input')?.addEventListener('change', sync));
  sync();
  // The button ships disabled so a no-JS or failed-script page cannot submit
  // a form with no native cart path; enable it once the handler is attached.
  if (button && form.dataset.available === 'true') button.disabled = false;

  // Returning from checkout restores the page from the back-forward cache
  // with the button still in its busy state; reset it.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || !button) return;
    button.disabled = form.dataset.available !== 'true';
    if (labelNode) labelNode.textContent = label;
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const input = selectedInput();
    if (!input || !button) return;

    const bags = Number(input.dataset.bags) || 1;
    const items = [{ id: Number(form.dataset.variantId), quantity: bags }];
    if (input.dataset.gifts === 'true') {
      (form.dataset.giftIds || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter(Boolean)
        .forEach((id) => items.push({ id, quantity: 1 }));
    }

    button.disabled = true;
    labelNode.textContent = 'Adding…';
    if (error) error.hidden = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const [base, ...extras] = items;
      const response = await setBundleLine(fetch, root, base);
      if (response.ok && extras.length) {
        const extraResponse = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ items: extras }),
        });
        if (!extraResponse.ok) throw new Error(`Gift add failed (${extraResponse.status})`);
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || `Cart add failed (${response.status})`);
      }
      const redirect = form.dataset.redirect || '/checkout';
      const code = (input.dataset.code || '').trim();
      window.location.href = code
        ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`
        : redirect;
    } catch (err) {
      console.error('[winks-bundle-buybox]', err);
      if (error) {
        error.textContent = 'Something went wrong adding this to your cart. Please try again.';
        error.hidden = false;
      }
      button.disabled = false;
      labelNode.textContent = label;
    }
  });
}

const initAll = (root = document) => root.querySelectorAll('[data-winks-bundle-form]').forEach(initBundleForm);
initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
